class AppButton extends HTMLElement {
    static get observedAttributes() {
        return [
            'disabled',
            'loading',
            'aria-label',
            'type',
            'variant',
            'size',
            'inline-size',
            'icon-only',
        ];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this._initialized = false;
    }

    // --- Property Getters/Setters ---
    get disabled() { return this.hasAttribute('disabled'); }
    set disabled(val) { val ? this.setAttribute('disabled', '') : this.removeAttribute('disabled'); }

    get loading() { return this.hasAttribute('loading'); }
    set loading(val) { val ? this.setAttribute('loading', '') : this.removeAttribute('loading'); }

    get ariaLabel() { return this.getAttribute('aria-label'); }
    set ariaLabel(val) { this.setAttribute('aria-label', val); }

    get type() { return this.getAttribute('type'); }
    set type(val) { this.setAttribute('type', val); }

    get variant() { return this.getAttribute('variant'); }
    set variant(val) { this.setAttribute('variant', val); }

    get size() { return this.getAttribute('size'); }
    set size(val) { this.setAttribute('size', val); }

    get inlineSize() { return this.getAttribute('inline-size'); }
    set inlineSize(val) { this.setAttribute('inline-size', val); }

    get iconOnly() { return this.hasAttribute('icon-only'); }
    set iconOnly(val) { val ? this.setAttribute('icon-only', '') : this.removeAttribute('icon-only'); }

    connectedCallback() {
        if (!this._initialized) {
            this._initialized = true;
            this._renderShell();
        }
        this._applyProps();
        this._slot = this.shadowRoot.querySelector('slot');
        this._button = this.shadowRoot.querySelector('button');
        this._slot.addEventListener('slotchange', this._handleSlotChange.bind(this));
        this._button.addEventListener('click', this._onClick.bind(this));
    }

    disconnectedCallback() {
        if (this._button) this._button.removeEventListener('click', this._onClick);
    }

    attributeChangedCallback() {
        this._applyProps();
    }

    // --- Internal Helpers ---
    _handleSlotChange() {
        if (this.hasAttribute('icon-only')) {
            this._button.classList.add('app-manager-button--icon-only');
            return;
        }
        const nodes = this._slot.assignedNodes({ flatten: true });
        const onlyIcon = nodes.length === 1 && nodes[0].nodeType === 1 && nodes[0].tagName === 'SVG';
        if (onlyIcon) {
            this._button.classList.add('app-manager-button--icon-only');
        } else {
            this._button.classList.remove('app-manager-button--icon-only');
        }
    }

    _renderShell() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: inline-block;
                }
                :host([inline-size="fill"]),
                :host([fullwidth]) {
                    width: 100%;
                    display: block;
                }

                .app-manager-button {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    height: 28px;
                    min-height: 28px;
                    padding: 6px 12px;
                    font-weight: 400;
                    font-family: Inter, -apple-system, system-ui, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
                    font-size: 12px;
                    line-height: 16px;
                    color: #fff;
                    background-color: #303030;
                    background-image: linear-gradient(rgba(48, 48, 48, 0) 63.53%, rgba(255, 255, 255, 0.15) 100%);
                    border: none;
                    border-radius: 8px;
                    box-shadow: #e3e3e3 0 1px 0 0 inset, #e3e3e3 1px 0 0 0 inset, #e3e3e3 -1px 0 0 0 inset, #b5b5b5 0 -1px 0 0 inset;
                    cursor: pointer;
                    user-select: none;
                    transition: background 0.15s, color 0.15s;
                    position: relative;
                }

                .app-manager-button .app-manager-button__content {
                    display: flex;
                }

                .app-manager-button--small {
                    min-height: 24px;
                    padding: 4px 10px;
                    font-size: 11px;
                }
                .app-manager-button--large {
                    min-height: 36px;
                    padding: 8px 16px;
                    font-size: 14px;
                }
                .app-manager-button--full {
                    width: 100%;
                }

                .app-manager-button:hover {
                    background-color: #fafafa;
                }
                .app-manager-button:active {
                    box-shadow: rgba(0, 0, 0, 0.15) 0 1px 2px inset;
                }
                .app-manager-button:focus-visible {
                    outline: 2px solid #5c6ac4;
                    outline-offset: 2px;
                }
                .app-manager-button:disabled,
                .app-manager-button--disabled {
                    cursor: not-allowed;
                    pointer-events: none;
                    background-color: #0000000d;
                    box-shadow: unset;
                    color: #b5b5b5;
                }
                .app-manager-button--loading {
                    pointer-events: none;
                }
                .app-manager-button__loader {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                .app-manager-button__spinner {
                    width: 16px;
                    height: 16px;
                    display: inline-block;
                    vertical-align: middle;
                    animation: button-spin 0.6s linear infinite;
                }
                @keyframes button-spin {
                    100% { transform: rotate(360deg); }
                }
                .app-manager-button--primary {
                    background-color: #303030;
                    background-image: linear-gradient(#30303000 63.53%, #ffffff26);
                    color: #fff;
                    border-bottom-color: #fff;
                    text-decoration: none;
                    box-shadow: #000000cc 0 -1px 0 1px inset, #303030 0 0 0 1px inset, #ffffff40 0 .5px 0 1.5px inset;
                }
                .app-manager-button--primary:hover {
                    background-color: #1a1a1a;
                    color: #fff;
                }
                .app-manager-button--primary:active {
                    background-color: #000;
                }
                .app-manager-button--secondary {
                    background-color: #fff;
                    color: #303030;
                }
                .app-manager-button--destructive {
                    background-color: #d82c0d;
                    color: #fff;
                    box-shadow: none;
                }
                .app-manager-button--destructive:hover {
                    background-color: #b42309;
                }
                .app-manager-button--destructive:active {
                    background-color: #8f1b07;
                }

                .app-manager-button__icon {
                    display: inline-flex;
                    margin-right: 6px;
                }
                .app-manager-button__icon--right {
                    margin-left: 6px;
                    margin-right: 0;
                }

                .app-manager-button--icon-only {
                    padding: 0;
                    width: 36px;
                    height: 36px;
                    min-width: 36px;
                    min-height: 36px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0;
                }
                .app-manager-button--icon-only svg {
                    width: 20px;
                    height: 20px;
                    display: block;
                }

                .app-manager-button-group {
                    display: inline-flex;
                    gap: 8px;
                }

                .app-manager-button * {
                    pointer-events: none;
                }

                @media (max-width: 480px) {
                    .app-manager-button {
                        font-size: 11px;
                        padding: 4px 8px;
                        min-height: 24px;
                    }
                    .app-manager-button--large {
                        font-size: 13px;
                        padding: 6px 12px;
                        min-height: 32px;
                    }
                    .app-manager-button--icon-only {
                        width: 32px;
                        height: 32px;
                        min-width: 32px;
                        min-height: 32px;
                    }
                }
            </style>
            <button type="button" class="app-manager-button">
                <span class="app-manager-button__content"><slot></slot></span>
            </button>
        `;
    }

    _applyProps() {
        if (!this.shadowRoot) return;
        this._button = this.shadowRoot.querySelector('button');
        if (!this._button) return;
        this._button.className = 'app-manager-button';
        const variant = this.getAttribute('variant') || 'secondary';
        this._button.classList.add(`app-manager-button--${variant}`);
        if (this.getAttribute('size') === 'small') this._button.classList.add('app-manager-button--small');
        if (this.getAttribute('size') === 'large') this._button.classList.add('app-manager-button--large');
        if (this.getAttribute('inline-size') === 'fill' || this.hasAttribute('fullwidth')) {
            this._button.classList.add('app-manager-button--full');
        }
        if (this.hasAttribute('disabled')) {
            this._button.setAttribute('disabled', '');
            this._button.classList.add('app-manager-button--disabled');
            this._button.setAttribute('aria-disabled', 'true');
        } else {
            this._button.removeAttribute('disabled');
            this._button.classList.remove('app-manager-button--disabled');
            this._button.setAttribute('aria-disabled', 'false');
        }
        if (this.hasAttribute('loading')) {
            this._button.classList.add('app-manager-button--loading');
            // Show loader, hide content
            const content = this._button.querySelector('.app-manager-button__content');
            if (content) content.style.display = 'none';
            let loader = this._button.querySelector('.app-manager-button__loader');
            if (!loader) {
                loader = document.createElement('span');
                loader.className = 'app-manager-button__loader';
                loader.innerHTML = `
                    <svg class="app-manager-button__spinner" viewBox="0 0 50 50">
                        <circle class="app-manager-button__spinner-bg" cx="25" cy="25" r="20" fill="none" stroke="#e3e3e3" stroke-width="5"/>
                        <circle class="app-manager-button__spinner-fg" cx="25" cy="25" r="20" fill="none" stroke="#1a1a1a" stroke-width="5" stroke-linecap="round" stroke-dasharray="90 130" stroke-dashoffset="0"/>
                    </svg>
                `;
                this._button.appendChild(loader);
            } else {
                loader.style.display = 'flex';
            }
        } else {
            this._button.classList.remove('app-manager-button--loading');
            // Hide loader, show content
            const content = this._button.querySelector('.app-manager-button__content');
            if (content) content.style.display = '';
            const loader = this._button.querySelector('.app-manager-button__loader');
            if (loader) loader.style.display = 'none';
        }
        if (this.hasAttribute('aria-label')) {
            this._button.setAttribute('aria-label', this.getAttribute('aria-label'));
        }
        if (this.hasAttribute('type')) {
            this._button.setAttribute('type', this.getAttribute('type'));
        }
        if (this.hasAttribute('icon-only')) {
            this._button.classList.add('app-manager-button--icon-only');
        } else {
            this._button.classList.remove('app-manager-button--icon-only');
        }
    }

    _onClick(e) {
        if (this.hasAttribute('disabled') || this.hasAttribute('loading')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }
        this.dispatchEvent(new Event('app-button-click', { bubbles: true, composed: true }));
    }
}

customElements.define('app-button', AppButton);

export { AppButton };